import React from 'react';
import {
	isInitial,
	isPending,
	isSuccess,
	isError,
	RequestState,
} from '../store-tools';
import { useSlackStore } from './store';
import { Autocomplete, AutocompleteRenderInputParams } from '@material-ui/lab';
import {
	CircularProgress,
	createStyles,
	IconButton,
	makeStyles,
	TextField,
	Typography,
} from '@material-ui/core';
import { SlackChannel } from '@contracts/platform';
import { UnreachableError } from '../errors';
import RefreshIcon from '@material-ui/icons/Refresh';

type SlackChannelInfo = Pick<SlackChannel, 'slackChannelId' | 'name'>;

const useStyles = makeStyles((_theme) => {
	return createStyles({
		hash: {
			marginLeft: `0.3em`,
		},
	});
});

export const useSlackChannelSelect = (opts?: {
	defaultValue?: string | SlackChannelInfo;
}) => {
	const [slackChannel, setSlackChannel] = React.useState<
		string | SlackChannelInfo
	>(opts?.defaultValue || 'remote-social');

	const inputRef = React.useRef<HTMLInputElement>();

	const displayListOfSlackChannels = React.useCallback(() => {
		if (!inputRef.current) {
			return;
		}
		inputRef.current.focus();
	}, []);

	return {
		slackChannel,
		displayListOfSlackChannels,
		slackChannelSelectProps: {
			inputRef,
			onChange: setSlackChannel,
			value: slackChannel,
		},
	};
};

type Props = {
	variant: 'select-existing' | 'select-or-create';
	inputRef?: React.MutableRefObject<HTMLInputElement | undefined>;

	value: string | SlackChannelInfo | undefined;
	onChange: (channelName: string | SlackChannelInfo) => void;
};

const channelNameRegExp = /^[a-z0-9-_][a-z0-9-_]{0,79}$/;

const getOptionLabel = (option: string | SlackChannelInfo): string =>
	typeof option === 'object' ? option.name : option;

const renderOption = (option: string | SlackChannelInfo): string =>
	typeof option === 'object' ? `# ${option.name}` : `# ${option}`;

const determineChannelStatus = (
	channel: string | SlackChannelInfo | undefined,
	listChannelsRequest: RequestState<SlackChannel[], SlackChannel[]>,
):
	| { status: 'unselected' | 'archived' | 'invalid'; entry?: undefined }
	| { status: boolean | 'unknown'; entry: SlackChannel | undefined } => {
	if (!channel) {
		return {
			status: 'unselected',
		};
	}
	const entry =
		typeof channel === 'object'
			? listChannelsRequest.data.find(
					(entry) => entry.slackChannelId === channel.slackChannelId,
			  )
			: listChannelsRequest.data.find((entry) => entry.name === channel);
	if (entry?.isArchived) {
		return {
			status: 'archived',
		};
	}
	if (
		typeof channel === 'string' &&
		!entry &&
		!channelNameRegExp.test(channel)
	) {
		return {
			status: 'invalid',
		};
	}
	return {
		status: isSuccess(listChannelsRequest) ? Boolean(entry) : 'unknown',
		entry,
	};
};

const helperText = (
	channelStatus: boolean | 'unknown' | 'unselected' | 'archived' | 'invalid',
) => {
	switch (channelStatus) {
		case true:
			return 'You are using an existing public channel';
		case false:
			return `The specified public channel doesn't exist yet and going to be created`;
		case 'archived':
			return `Specified channel is archived, you can manually unarchive it in Slack, or choose a different name`;
		case 'invalid':
			// straight from Slack UI
			return `Channel names can’t contain spaces, periods, or most punctuation`;
		case 'unknown':
		case 'unselected':
			return `Enter the name of a public channel to be created, or pick an existing one`;
		default:
			throw new UnreachableError(channelStatus);
	}
};

export const SlackChannelSelect: React.ComponentType<Props> = React.memo(
	({ variant, inputRef, value, onChange: onChangeProp }) => {
		const styles = useStyles();
		const { listChannelsRequest, listChannels: triggerLoadChannels } =
			useSlackStore();

		React.useEffect(() => {
			if (isInitial(listChannelsRequest)) {
				triggerLoadChannels();
			}
		}, [listChannelsRequest, triggerLoadChannels]);

		const onChange = React.useCallback(
			(_: unknown, channel: string | SlackChannelInfo | null) => {
				onChangeProp(channel || '');
			},
			[onChangeProp],
		);
		const [inputValue, setInputValue] = React.useState(
			typeof value === 'object' ? value.name : value,
		);
		const onInputChange = React.useCallback(
			(_: unknown, value: string) => {
				setInputValue(value);
			},
			[setInputValue],
		);

		const onBlur = React.useCallback(() => {
			if (value !== inputValue) {
				const { entry } = determineChannelStatus(
					inputValue,
					listChannelsRequest,
				);
				onChangeProp(entry ?? inputValue ?? '');
			}
		}, [value, inputValue, listChannelsRequest, onChangeProp]);

		const channelStatus = React.useMemo(
			() => determineChannelStatus(value, listChannelsRequest),
			[listChannelsRequest, value],
		);

		React.useEffect(() => {
			if (
				channelStatus.status === true &&
				channelStatus.entry &&
				channelStatus.entry !== value
			) {
				// we just got a response from BE
				// that this channel exists, so we can
				// use channel id
				onChangeProp(channelStatus.entry);
			}
		}, [channelStatus, onChangeProp, value]);

		const loading = isPending(listChannelsRequest);
		const error = isError(listChannelsRequest);

		const renderInput = React.useCallback(
			(params: AutocompleteRenderInputParams) => (
				<TextField
					{...params}
					variant="outlined"
					size="small"
					fullWidth
					label="Channel"
					error={['unselected', 'archived', 'invalid'].includes(
						String(channelStatus.status),
					)}
					helperText={helperText(channelStatus.status)}
					InputProps={{
						...params.InputProps,
						inputRef,
						startAdornment: (
							<Typography className={styles.hash}>#</Typography>
						),
						endAdornment: (
							<>
								{loading && (
									<CircularProgress
										size="1em"
										color="inherit"
									/>
								)}
								{error && (
									<IconButton
										onClick={triggerLoadChannels}
										size="small"
									>
										<RefreshIcon fontSize="small" />
									</IconButton>
								)}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			),
			[
				channelStatus.status,
				inputRef,
				styles.hash,
				loading,
				error,
				triggerLoadChannels,
			],
		);

		const unarchived = React.useMemo(
			(): SlackChannelInfo[] =>
				listChannelsRequest.data.filter(
					(channel) => !channel.isArchived,
				),
			[listChannelsRequest.data],
		);

		return (
			<Autocomplete
				size="small"
				value={value ?? ''}
				inputValue={inputValue}
				options={unarchived}
				getOptionLabel={getOptionLabel}
				// We use loadingText for error purpose because noOptionsText is not displayed for some reason
				loading={error || loading}
				loadingText={
					error
						? 'Failed to list existing public channels, you can still try to create a new public channel or try again later'
						: 'Loading...'
				}
				onChange={onChange}
				onInputChange={onInputChange}
				freeSolo={variant === 'select-or-create'}
				renderOption={renderOption}
				renderInput={renderInput}
				openOnFocus={true}
				onBlur={onBlur}
			/>
		);
	},
);
